import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { ImportPreview, ImportOutcome, ImportSource, ManualCorrection } from "../../application/services/equipment-import-service.js";
import type { ImportIssue } from "../../domain/import/equipment-import.js";
import type { StagingRecord } from "../../domain/import/import-staging.js";
import { projectOptionsForWorkOrder, unitAreaOptionsForProject } from "../../domain/rdo/sub-os-form-cascade.js";

interface Option { id: string; label: string }

/**
 * IMPORTAÇÃO INTELIGENTE DE SISTEMAS E TUBULAÇÕES (Bloco 04).
 *
 * Fluxo obrigatório: ARQUIVO → CLIENTE → OS → PROJETO → UNIDADE/ÁREA →
 * ANÁLISE → PREVIEW → CONFIRMAÇÃO. Aceita .xlsx e PDF pesquisável; os dois
 * caminhos convergem para o mesmo modelo intermediário e para a mesma
 * validação. Nada é gravado antes da confirmação.
 */
class AppEquipmentImportPage extends HTMLElement {
  private clients: Option[] = [];
  private workOrders: Array<Option & { clientId: string }> = [];
  private workOrderLinks: unknown[] = [];
  private projects: Array<Record<string, unknown>> = [];
  private unitAreas: Array<Record<string, unknown>> = [];

  private clientId = "";
  private serviceOrderId = "";
  private projectId = "";
  private unitAreaId = "";

  private fileName = "";
  private source: ImportSource | null = null;
  private preview: ImportPreview | null = null;
  private outcome: ImportOutcome | null = null;
  private excludedRows = new Set<number>();
  private corrections: ManualCorrection[] = [];

  private busy: "" | "reading" | "previewing" | "importing" = "";
  private message: { kind: "ok" | "error" | "info"; text: string } | null = null;
  private showAllIssues = false;

  async connectedCallback() {
    this.render();
    await this.loadClients();
  }

  private get platform(): any {
    return (window as any).app?.platform;
  }

  private ctx(): any {
    const current = contextStore.get();
    return {
      activeClientId: this.clientId || current.activeClientId || undefined,
      correlationId: "",
      userId: current.userId,
      professionalId: current.professionalId || null,
      permissions: current.permissions
    };
  }

  private can(permission: string): boolean {
    return contextStore.hasPermission(permission as any);
  }

  // ------------------------------------------------------------------ cascata

  private async loadClients() {
    try {
      const clients: any[] = (await this.platform?.adminService?.listAuthorizedClients(this.ctx()).catch(() => [])) || [];
      this.clients = clients.map(c => ({ id: String(c.id), label: c.fantasyName || c.name || String(c.id) }));
      if (!this.clientId && this.clients.length === 1) {
        this.clientId = this.clients[0]!.id;
        await this.loadWorkOrders();
      }
    } catch (error) {
      console.warn("[IMPORTAÇÃO] Falha ao carregar Clientes", error);
    }
    this.render();
  }

  /** OS do Cliente escolhido. */
  private async loadWorkOrders() {
    const orders: any[] = (await this.platform?.adminService?.listWorkOrders(this.ctx(), this.clientId || undefined).catch(() => [])) || [];
    this.workOrders = orders.map(o => ({
      id: String(o.id),
      label: [o.code, o.description].filter(Boolean).join(" - ") || String(o.id),
      clientId: String(o.clientId || "")
    }));
    this.workOrderLinks = (await this.platform?.rdoService?.listWorkOrderProjectLinks(this.ctx()).catch(() => [])) || [];
    await this.loadProjects();
  }

  /**
   * Projetos compatíveis com Cliente + OS. Reaproveita a cascata oficial já
   * usada no cadastro de Sub-OS, em vez de recriar a regra do vínculo.
   */
  private async loadProjects() {
    const all: any[] = (await this.platform?.projectService?.listProjects(this.clientId || undefined).catch(() => [])) || [];
    this.projects = this.serviceOrderId
      ? (projectOptionsForWorkOrder(this.serviceOrderId, this.workOrders as any, all as any, this.workOrderLinks as any) as any)
      : [];
    await this.loadUnitAreas();
  }

  private async loadUnitAreas() {
    const filter: any = {};
    if (this.clientId) filter.clientId = this.clientId;
    if (this.projectId) filter.projectId = this.projectId;
    const units: any[] = (await this.platform?.projectService?.listUnitAreas(Object.keys(filter).length ? filter : undefined).catch(() => [])) || [];
    this.unitAreas = this.projectId ? (unitAreaOptionsForProject(this.projectId, units as any) as any) : [];
  }

  private targetComplete(): boolean {
    return Boolean(this.clientId && this.serviceOrderId && this.projectId && this.unitAreaId);
  }

  private resetResults() {
    this.preview = null;
    this.outcome = null;
    this.showAllIssues = false;
    this.excludedRows.clear();
    this.corrections = [];
  }

  // ------------------------------------------------------------------ arquivo

  private async onFileSelected(file: File | null) {
    this.resetResults();
    this.source = null;
    this.fileName = file ? file.name : "";
    this.message = null;
    if (!file) { this.render(); return; }

    this.busy = "reading";
    this.render();
    try {
      this.source = { filename: file.name, bytes: new Uint8Array(await file.arrayBuffer()) };
      if (this.targetComplete()) await this.runPreview();
      else this.message = { kind: "info", text: "Arquivo carregado. Complete o destino para analisar." };
    } catch (error: any) {
      this.source = null;
      this.message = { kind: "error", text: error?.safeMessage || error?.message || "Não foi possível ler o arquivo." };
    } finally {
      this.busy = "";
      this.render();
    }
  }

  private target() {
    return { clientId: this.clientId, serviceOrderId: this.serviceOrderId, projectId: this.projectId, unitAreaId: this.unitAreaId };
  }

  private async runPreview() {
    if (!this.source || !this.targetComplete()) return;
    this.busy = "previewing";
    this.outcome = null;
    this.render();
    try {
      this.preview = await this.platform.equipmentImportService.preview(this.ctx(), this.source, this.target(), {
        excludedRows: [...this.excludedRows],
        corrections: this.corrections
      });
      this.message = null;
    } catch (error: any) {
      this.preview = null;
      this.message = { kind: "error", text: error?.safeMessage || error?.message || "Não foi possível analisar o arquivo." };
    } finally {
      this.busy = "";
      this.render();
    }
  }

  private async runImport() {
    if (!this.source || !this.preview || !this.preview.canConfirm) return;
    const novos = this.preview.systems.novos + this.preview.piping.novos;
    if (!window.confirm(`Confirmar a importação de ${novos} registro(s) novo(s)?`)) return;

    this.busy = "importing";
    this.render();
    try {
      this.outcome = await this.platform.equipmentImportService.execute(this.ctx(), this.source, this.target(), {
        excludedRows: [...this.excludedRows],
        corrections: this.corrections
      });
      const created = this.outcome!.createdSystems + this.outcome!.createdPiping;
      const completed = this.outcome!.complementedSystems + this.outcome!.complementedPiping;
      this.message = {
        kind: "ok",
        text: `Importação concluída: ${created} registro(s) criado(s)`
          + (completed > 0 ? ` e ${completed} cadastro(s) existente(s) completado(s)` : "")
          + `. Lote ${this.outcome!.batchId}.`
      };
      this.preview = null;
    } catch (error: any) {
      this.message = { kind: "error", text: error?.safeMessage || error?.message || "Não foi possível concluir a importação." };
    } finally {
      this.busy = "";
      this.render();
    }
  }

  // ------------------------------------------------------------------- render

  private render() {
    if (!this.can(PermissionCode.EquipmentImport)) {
      this.innerHTML = `<style>${this.styles()}</style>
        <div class="imp">${this.headerHtml()}
          <div class="card empty"><div class="empty-title">Sem permissão</div>
          <div class="empty-desc">Seu perfil não tem permissão para importar Sistemas e Tubulações.</div></div>
        </div>`;
      return;
    }

    this.innerHTML = `<style>${this.styles()}</style>
      <div class="imp">
        ${this.headerHtml()}
        ${this.message ? `<div class="notice notice-${this.message.kind}">${this.esc(this.message.text)}</div>` : ""}
        ${this.targetHtml()}
        ${this.fileHtml()}
        ${this.busy === "previewing" ? this.skeleton("Analisando o arquivo…") : ""}
        ${this.busy === "importing" ? this.skeleton("Gravando o lote…") : ""}
        ${this.preview && this.busy === "" ? this.previewHtml(this.preview) : ""}
        ${this.outcome && this.busy === "" ? this.outcomeHtml(this.outcome) : ""}
      </div>`;
    this.bind();
  }

  private headerHtml(): string {
    return `
      <header class="imp-head">
        <nav class="crumb"><span class="crumb-root">EQUIPAMENTOS &amp; ATIVOS</span><span class="crumb-sep">›</span><span class="crumb-current">IMPORTAÇÃO</span></nav>
        <h1 class="imp-title">Importação de Sistemas e Tubulações</h1>
        <p class="imp-sub">Aceita planilha .xlsx e PDF pesquisável. Nada é gravado antes da sua confirmação.</p>
      </header>`;
  }

  private targetHtml(): string {
    const orders = this.clientId ? this.workOrders.filter(o => o.clientId === this.clientId) : [];
    return `
      <section class="card">
        <div class="step">1 · Destino do cadastro</div>
        <p class="step-desc">Cliente → OS → Projeto → Unidade/Área. Todo equipamento importado nasce com esse contexto.</p>
        <div class="grid grid-4">
          <label class="field">
            <span class="field-label">Cliente *</span>
            <select class="ctl" id="f-client">
              <option value="">Selecione…</option>
              ${this.clients.map(c => `<option value="${this.esc(c.id)}" ${this.clientId === c.id ? "selected" : ""}>${this.esc(c.label)}</option>`).join("")}
            </select>
          </label>
          <label class="field">
            <span class="field-label">Ordem de Serviço *</span>
            <select class="ctl" id="f-os" ${this.clientId ? "" : "disabled"}>
              <option value="">Selecione…</option>
              ${orders.map(o => `<option value="${this.esc(o.id)}" ${this.serviceOrderId === o.id ? "selected" : ""}>${this.esc(o.label)}</option>`).join("")}
            </select>
          </label>
          <label class="field">
            <span class="field-label">Projeto *</span>
            <select class="ctl" id="f-project" ${this.serviceOrderId ? "" : "disabled"}>
              <option value="">Selecione…</option>
              ${this.projects.map(p => `<option value="${this.esc(p["id"])}" ${this.projectId === String(p["id"]) ? "selected" : ""}>${this.esc([p["code"], p["name"]].filter(Boolean).join(" - "))}</option>`).join("")}
            </select>
          </label>
          <label class="field">
            <span class="field-label">Unidade / Área *</span>
            <select class="ctl" id="f-unit" ${this.projectId ? "" : "disabled"}>
              <option value="">Selecione…</option>
              ${this.unitAreas.map(u => `<option value="${this.esc(u["id"])}" ${this.unitAreaId === String(u["id"]) ? "selected" : ""}>${this.esc([u["code"], u["name"]].filter(Boolean).join(" - "))}</option>`).join("")}
            </select>
          </label>
        </div>
      </section>`;
  }

  private fileHtml(): string {
    return `
      <section class="card">
        <div class="step">2 · Arquivo</div>
        <p class="step-desc">Planilha .xlsx ou PDF pesquisável. As seções Sistemas e Tubulações são localizadas pelo nome.</p>
        <div class="grid">
          <label class="field field-wide">
            <span class="field-label">Arquivo *</span>
            <input class="ctl" type="file" id="f-file" accept=".xlsx,.pdf" ${this.targetComplete() ? "" : "disabled"}>
            ${this.targetComplete() ? "" : `<span class="hint">Complete Cliente, OS, Projeto e Unidade/Área antes de escolher o arquivo.</span>`}
          </label>
          <div class="field">
            <span class="field-label">Selecionado</span>
            <div class="chip">${this.busy === "reading" ? "Lendo…" : this.esc(this.fileName || "—")}</div>
          </div>
        </div>
      </section>`;
  }

  private previewHtml(preview: ImportPreview): string {
    const issues = this.showAllIssues ? preview.issues : preview.issues.slice(0, 20);
    const missing = [...new Set([...preview.systems.missingColumns, ...preview.piping.missingColumns])];
    const conflicting = preview.records.filter(r => r.disposition === "CONFLITO");
    return `
      <section class="card">
        <div class="step">3 · Conferência (staging)</div>
        <p class="step-desc">Formato: ${this.esc(preview.sourceFormat)} · Hash do arquivo: ${this.esc(preview.sourceHash.slice(0, 16))}…</p>

        ${preview.previousRuns.length > 0 ? `<div class="notice notice-info">Este mesmo arquivo já foi importado ${preview.previousRuns.length}x neste contexto. Registros existentes são reaproveitados, não duplicados.</div>` : ""}

        <div class="stats">
          ${this.statHtml("Sistemas", preview.systems)}
          ${this.statHtml("Tubulações", preview.piping)}
          <div class="stat">
            <div class="stat-label">Críticas</div>
            <div class="stat-value">${preview.errorCount} erro(s)</div>
            <div class="stat-sub">${preview.warningCount} aviso(s)</div>
          </div>
        </div>

        ${missing.length > 0 ? `<div class="notice notice-info">Colunas não encontradas (o campo fica vazio, nada é presumido): ${this.esc(missing.join(", "))}.</div>` : ""}

        ${this.linesPreviewHtml(preview)}

        ${conflicting.length > 0 ? this.conflictsHtml(conflicting) : ""}

        ${preview.issues.length > 0 ? `
          <table class="issues">
            <thead><tr><th>Linha</th><th>Coluna</th><th>Severidade</th><th>Descrição</th></tr></thead>
            <tbody>
              ${issues.map((issue: ImportIssue) => `
                <tr>
                  <td>${issue.rowNumber}</td>
                  <td>${this.esc(issue.column || "—")}</td>
                  <td><span class="tag tag-${issue.severity.toLowerCase()}">${issue.severity === "ERROR" ? "ERRO" : "AVISO"}</span></td>
                  <td>${this.esc(issue.message)}</td>
                </tr>`).join("")}
            </tbody>
          </table>
          ${preview.issues.length > issues.length ? `<button class="link-action" id="btn-all-issues" type="button">Ver todas as ${preview.issues.length} críticas</button>` : ""}
        ` : `<div class="notice notice-ok">Nenhuma crítica encontrada no arquivo.</div>`}

        ${preview.blockingReasons.length > 0 ? `
          <div class="notice notice-error">
            <strong>Confirmação bloqueada:</strong>
            <ul>${preview.blockingReasons.map(reason => `<li>${this.esc(reason)}</li>`).join("")}</ul>
          </div>` : ""}

        <div class="actions">
          <button class="btn btn-primary" id="btn-import" type="button" ${preview.canConfirm ? "" : "disabled"}>
            Confirmar importação (${preview.systems.novos + preview.piping.novos} novo(s))
          </button>
          <button class="btn" id="btn-reanalyze" type="button">Analisar novamente</button>
        </div>
      </section>`;
  }

  /**
   * Preview por Linha (comando "correção da importação", item 11): TAG,
   * FLUIDO, CÓDIGO DO FLUIDO, DESENHO e FLUXOGRAMA exatamente como serão
   * gravados nos campos oficiais da Tubulação — para o usuário confirmar
   * antes de CONFIRMAR IMPORTAÇÃO. Limitado às primeiras linhas para não
   * travar a tela com milhares de registros; o resumo acima já mostra o
   * total real.
   */
  private linesPreviewHtml(preview: ImportPreview): string {
    const lines = preview.records.filter(r => r.kind === "PIPING_LINE" && r.selected && r.disposition !== "BLOQUEADO");
    if (lines.length === 0) return "";
    const shown = lines.slice(0, 50);
    // ISOMÉTRICO é reconhecido no arquivo (extras) mas não tem campo oficial
    // em Equipment hoje — reportar a divergência em vez de descartar
    // silenciosamente ou inventar um campo de domínio (item 8).
    const isometricDetected = lines.filter(record => this.isometricValueOf(record) !== null);
    return `
      <div class="step" style="margin-top: 4px;">Prévia das Linhas (${lines.length})</div>
      <table class="issues">
        <thead><tr><th>Linha</th><th>TAG</th><th>Fluido</th><th>Código do Fluido</th><th>Desenho</th><th>Fluxograma</th></tr></thead>
        <tbody>
          ${shown.map(record => `
            <tr>
              <td>${record.rowNumber}</td>
              <td>${this.esc(record.draft.tag)}</td>
              <td>${this.esc(record.draft.fluid || "NÃO INFORMADO")}</td>
              <td>${this.esc(record.draft.fluidCode || "NÃO INFORMADO")}</td>
              <td>${this.esc(record.draft.drawingReference || "NÃO INFORMADO")}</td>
              <td>${this.esc(record.draft.flowDiagramReference || "NÃO INFORMADO")}</td>
            </tr>`).join("")}
        </tbody>
      </table>
      ${lines.length > shown.length ? `<p class="hint">Exibindo ${shown.length} de ${lines.length} Linhas. O total exato consta no resumo acima.</p>` : ""}
      ${isometricDetected.length > 0 ? `
        <div class="notice notice-info">
          <strong>ISOMÉTRICO detectado em ${isometricDetected.length} Linha(s), mas não existe campo oficial correspondente no cadastro da Tubulação.</strong>
          O valor foi lido do arquivo e fica preservado apenas como referência de origem — não é gravado em nenhum campo do cadastro. Solicite a criação do campo oficial se este dado precisar ser persistido.
        </div>` : ""}
    `;
  }

  /** Valor de ISOMÉTRICO detectado no arquivo (extras), quando houver — não existe campo oficial em Equipment (item 8). */
  private isometricValueOf(record: StagingRecord): string | null {
    const extras = record.draft.extras || {};
    for (const [header, value] of Object.entries(extras)) {
      if (/isom[eé]tric/i.test(header) || header.trim().toUpperCase() === "ISO") return value || null;
    }
    return null;
  }

  /** Conflito nunca é resolvido sozinho: mostra os dois valores e exige decisão. */
  private conflictsHtml(records: readonly StagingRecord[]): string {
    return `
      <div class="notice notice-info"><strong>Conflito com dado já cadastrado.</strong> Nada é sobrescrito automaticamente. Retire a linha do lote ou ajuste o cadastro.</div>
      <table class="issues">
        <thead><tr><th>Linha</th><th>TAG</th><th>Campo</th><th>Valor atual</th><th>Valor importado</th><th>Ação</th></tr></thead>
        <tbody>
          ${records.slice(0, 30).flatMap(record => record.conflicts.map(conflict => `
            <tr>
              <td>${record.rowNumber}</td>
              <td>${this.esc(record.tag)}</td>
              <td>${this.esc(conflict.label)}</td>
              <td>${this.esc(conflict.currentValue)}</td>
              <td>${this.esc(conflict.importedValue)}</td>
              <td><button class="link-action" data-exclude="${record.rowNumber}" type="button">${this.excludedRows.has(record.rowNumber) ? "NO LOTE" : "RETIRAR DO LOTE"}</button></td>
            </tr>`)).join("")}
        </tbody>
      </table>`;
  }

  private statHtml(label: string, summary: ImportPreview["systems"]): string {
    return `
      <div class="stat">
        <div class="stat-label">${this.esc(label)}</div>
        <div class="stat-value">${summary.novos} novo(s)</div>
        <div class="stat-sub">${summary.total} no arquivo · ${summary.existentes} já existem · ${summary.conflitos} conflito(s) · ${summary.bloqueados} bloqueado(s)</div>
      </div>`;
  }

  private outcomeHtml(outcome: ImportOutcome): string {
    return `
      <section class="card">
        <div class="step">Resultado do lote</div>
        <p class="step-desc">Lote ${this.esc(outcome.batchId)}</p>
        <div class="stats">
          <div class="stat"><div class="stat-label">Sistemas criados</div><div class="stat-value">${outcome.createdSystems}</div></div>
          <div class="stat"><div class="stat-label">Tubulações criadas</div><div class="stat-value">${outcome.createdPiping}</div></div>
          <div class="stat"><div class="stat-label">Reaproveitados</div><div class="stat-value">${outcome.reusedSystems + outcome.reusedPiping}</div></div>
          <div class="stat"><div class="stat-label">Cadastros completados</div><div class="stat-value">${outcome.complementedSystems + outcome.complementedPiping}</div></div>
        </div>
        ${outcome.complementedFields.length > 0
          // O backfill precisa dizer O QUE completou: "12 completados" sem
          // os campos não permite conferir se o que faltava entrou mesmo.
          ? `<p class="step-desc" style="margin-top:10px;">Campos preenchidos em cadastros que já existiam: ${this.esc(outcome.complementedFields.join(", "))}. Nenhum valor preenchido foi sobrescrito.</p>`
          : ""}
      </section>`;
  }

  private skeleton(label: string): string {
    return `<div class="card sk"><div class="sk-label">${this.esc(label)}</div><div class="sk-line"></div><div class="sk-line w60"></div></div>`;
  }

  // ------------------------------------------------------------------- events

  private bind() {
    const client = this.querySelector("#f-client") as HTMLSelectElement | null;
    client?.addEventListener("change", async () => {
      this.clientId = client.value;
      this.serviceOrderId = ""; this.projectId = ""; this.unitAreaId = "";
      this.resetResults();
      await this.loadWorkOrders();
      this.render();
    });

    const os = this.querySelector("#f-os") as HTMLSelectElement | null;
    os?.addEventListener("change", async () => {
      this.serviceOrderId = os.value;
      this.projectId = ""; this.unitAreaId = "";
      this.resetResults();
      await this.loadProjects();
      this.render();
    });

    const project = this.querySelector("#f-project") as HTMLSelectElement | null;
    project?.addEventListener("change", async () => {
      this.projectId = project.value;
      this.unitAreaId = "";
      this.resetResults();
      await this.loadUnitAreas();
      this.render();
    });

    const unit = this.querySelector("#f-unit") as HTMLSelectElement | null;
    unit?.addEventListener("change", async () => {
      this.unitAreaId = unit.value;
      this.resetResults();
      this.render();
      if (this.source && this.targetComplete()) await this.runPreview();
    });

    (this.querySelector("#f-file") as HTMLInputElement | null)
      ?.addEventListener("change", e => { void this.onFileSelected((e.target as HTMLInputElement).files?.[0] || null); });

    this.querySelector("#btn-import")?.addEventListener("click", () => { void this.runImport(); });
    this.querySelector("#btn-reanalyze")?.addEventListener("click", () => { void this.runPreview(); });
    this.querySelector("#btn-all-issues")?.addEventListener("click", () => { this.showAllIssues = true; this.render(); });
    this.querySelectorAll<HTMLElement>("[data-exclude]").forEach(element => {
      element.addEventListener("click", () => {
        const row = Number(element.dataset["exclude"]);
        if (this.excludedRows.has(row)) this.excludedRows.delete(row);
        else this.excludedRows.add(row);
        void this.runPreview();
      });
    });
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  private styles(): string {
    return `
      .imp { display: flex; flex-direction: column; gap: 16px; }
      .crumb { display: flex; align-items: center; gap: 8px; font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; }
      .crumb-root { color: var(--ia-fg-soft, #9BA4AE); }
      .crumb-sep { color: var(--ia-fg-muted, rgba(255,255,255,0.45)); }
      .crumb-current { color: var(--ia-fg, #C8D2DC); font-weight: 600; }
      .imp-title { margin: 6px 0 2px; font-size: clamp(1.4rem, 2.2vw, 1.9rem); font-weight: 600; color: var(--ia-fg, #E7EAED); }
      .imp-sub { margin: 0; color: var(--ia-fg-muted, #8A939D); font-size: 0.875rem; }

      .card { background: var(--ia-bg-panel, #12161A); border: 1px solid var(--ia-line, rgba(255,255,255,0.08)); border-radius: 10px; padding: 16px; }
      .step { font-size: 0.7rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ia-warning-fg, #C9A227); font-weight: 700; }
      .step-desc { margin: 4px 0 14px; color: var(--ia-fg-muted, #8A939D); font-size: 0.8rem; }

      .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
      .grid-4 { grid-template-columns: repeat(4, 1fr); }
      .field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
      .field-wide { grid-column: span 2; }
      .field-label { font-size: 0.68rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); }
      .ctl { width: 100%; padding: 9px 10px; border-radius: 7px; background: var(--ia-bg-inset, #0E1216); border: 1px solid var(--ia-line, rgba(255,255,255,0.10)); color: var(--ia-fg, #DCE2E8); font-size: 0.82rem; font-family: inherit; }
      .ctl:disabled { opacity: 0.45; }
      .hint { font-size: 0.7rem; color: var(--ia-fg-muted, #8A939D); }
      .chip { padding: 9px 10px; border-radius: 7px; background: var(--ia-bg-inset, #0E1216); border: 1px dashed var(--ia-line, rgba(255,255,255,0.12)); color: var(--ia-fg-soft, #C3CAD1); font-size: 0.8rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

      .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 14px; }
      .stat { background: var(--ia-bg-inset, #0E1216); border: 1px solid var(--ia-line, rgba(255,255,255,0.07)); border-radius: 8px; padding: 12px; }
      .stat-label { font-size: 0.66rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); }
      .stat-value { font-size: 1.25rem; font-weight: 600; color: var(--ia-fg, #E7EAED); margin-top: 4px; }
      .stat-sub { font-size: 0.72rem; color: var(--ia-fg-muted, #8A939D); margin-top: 2px; }

      .notice { padding: 10px 12px; border-radius: 8px; margin-bottom: 12px; font-size: 0.82rem; }
      .notice-ok { background: rgba(110,158,126,0.14); color: var(--ia-fg, #B7D8C2); border: 1px solid rgba(110,158,126,0.3); }
      .notice-error { background: rgba(167,111,111,0.14); color: var(--ia-danger-fg, #E3B9B9); border: 1px solid rgba(167,111,111,0.3); }
      .notice-info { background: rgba(110,132,158,0.14); color: var(--ia-fg-soft, #BCCDE0); border: 1px solid rgba(110,132,158,0.3); }

      .issues { width: 100%; border-collapse: collapse; font-size: 0.78rem; margin-bottom: 10px; }
      .issues th { text-align: left; font-size: 0.62rem; letter-spacing: 0.07em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); padding: 6px 8px; border-bottom: 1px solid var(--ia-line, rgba(255,255,255,0.08)); }
      .issues td { padding: 7px 8px; border-bottom: 1px solid var(--ia-line, rgba(255,255,255,0.05)); color: var(--ia-fg-soft, #C3CAD1); vertical-align: top; }
      .tag { display: inline-block; padding: 2px 7px; border-radius: 999px; font-size: 0.6rem; font-weight: 700; letter-spacing: 0.06em; }
      .tag-error { background: rgba(167,111,111,0.2); color: var(--ia-danger-fg, #E3B9B9); }
      .tag-warning { background: rgba(198,168,106,0.18); color: var(--ia-warning-fg, #E0CFA3); }

      .actions { display: flex; gap: 10px; margin-top: 14px; flex-wrap: wrap; }
      .btn { padding: 9px 16px; border-radius: 8px; font-size: 0.78rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; cursor: pointer; background: var(--ia-bg-panel, #1A2026); color: var(--ia-fg-soft, #C3CAD1); border: 1px solid var(--ia-line, rgba(255,255,255,0.10)); }
      .btn:disabled { opacity: 0.45; cursor: not-allowed; }
      .btn-primary { background: var(--ia-bg-raised, #2B3A47); color: var(--ia-fg, #E7EAED); border-color: var(--ia-line, #3A4C5C); }
      .btn-primary:hover:not(:disabled) { background: var(--ia-bg-raised, #334554); }
      .link-action { background: none; border: none; padding: 0; font-size: 0.72rem; font-weight: 600; color: var(--ia-info-fg, #9BB4CC); cursor: pointer; }
      .link-action:hover { text-decoration: underline; }

      .empty { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 6px; padding: 28px 12px; color: var(--ia-fg-muted, #8A939D); }
      .empty-title { font-size: 0.85rem; color: var(--ia-fg-soft, #C3CAD1); font-weight: 600; }
      .empty-desc { font-size: 0.75rem; max-width: 420px; }

      .sk { display: flex; flex-direction: column; gap: 10px; }
      .sk-label { font-size: 0.78rem; color: var(--ia-fg-muted, #8A939D); }
      .sk-line { height: 12px; border-radius: 4px; background: linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.10), rgba(255,255,255,0.05)); background-size: 200% 100%; animation: sk 1.2s ease-in-out infinite; }
      .sk-line.w60 { width: 60%; }
      @keyframes sk { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

      @media (max-width: 900px) {
        .grid, .grid-4, .stats { grid-template-columns: 1fr; }
        .field-wide { grid-column: span 1; }
      }`;
  }
}

if (!customElements.get("app-equipment-import-page")) {
  customElements.define("app-equipment-import-page", AppEquipmentImportPage);
}
